import React from 'react';
import { ActivityIndicator, Pressable, RefreshControl, View } from 'react-native';
import { router } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';

import { ThemedText } from '@/components/themed-text';
import { PageLayout } from '@/components/ui/page-layout';
import { PressableScale } from '@/components/ui/pressable-scale';
import { EmptyState } from '@/components/ui/empty-state';
import {
  useApproveRecurrence,
  useProviderPendingRecurrences,
  useRejectRecurrence,
} from '@/hooks/use-recurrences';
import { useFocusParam } from '@/hooks/use-focus-param';
import { FOCUS_PARAM } from '@/lib/notification-navigation';
import { RecurrenceSummary } from '@/types/recurrence';
import { useToastStore } from '@/store/toastStore';
import { showPlanLimit } from '@/store/planLimitStore';
import { formatCurrency } from '@/utils/format-currency';
import { recurrencePeriodLabel, recurrenceScheduleLines } from '@/utils/format-recurrence';
import { useIconChip, useThemedHex } from '@/hooks/use-theme-colors';

function PendingCard({ item, focused }: { item: RecurrenceSummary; focused?: boolean }) {
  const th = useThemedHex();
  const chip = useIconChip();
  const approve = useApproveRecurrence();
  const reject = useRejectRecurrence();
  const { addToast } = useToastStore();
  const busy = approve.isPending || reject.isPending;

  const onApprove = () =>
    approve.mutate(item.id, {
      onSuccess: () => addToast('Recorrência aprovada.', 'success'),
      onError: (e) => {
        if (!showPlanLimit(e)) addToast('Falha ao aprovar.', 'error');
      },
    });
  const onReject = () =>
    reject.mutate(
      { enrollmentId: item.id },
      {
        onSuccess: () => addToast('Recorrência recusada.', 'success'),
        onError: () => addToast('Falha ao recusar.', 'error'),
      },
    );

  const lines = recurrenceScheduleLines(item);

  return (
    <View
      className="gap-3 rounded-2xl bg-card p-4"
      style={{
        borderCurve: 'continuous',
        borderWidth: focused ? 2 : 1,
        borderColor: focused ? '#FD5001' : th('#E5E5E5'),
      }}
    >
      {focused && (
        <View className="self-start rounded-full bg-primary/10 px-2.5 py-1">
          <ThemedText className="font-semibold text-[11px] text-primary">
            Pedido da notificação
          </ThemedText>
        </View>
      )}

      <View className="flex-row items-center gap-3">
        <View
          className="h-10 w-10 items-center justify-center rounded-2xl"
          style={{ backgroundColor: chip('#FD5001', '1A').bg, borderCurve: 'continuous' }}
        >
          <Ionicons name="repeat" size={20} color={chip('#FD5001', '1A').fg} />
        </View>
        <View className="flex-1">
          <ThemedText className="font-bold text-base text-neutral-900">
            {item.service?.name ?? 'Serviço'}
          </ThemedText>
          <ThemedText className="text-xs text-neutral-500">
            {item.client?.user?.username ?? 'Cliente'} quer marcar de forma recorrente
          </ThemedText>
        </View>
      </View>

      {/* Preview da agenda pedida: o prestador decide vendo os horários, não o nome. */}
      <View className="gap-1.5 rounded-xl bg-neutral-50 p-3">
        {lines.length > 0 ? (
          lines.map((line, i) => (
            <View key={`${line}-${i}`} className="flex-row items-center gap-2">
              <Ionicons className="text-neutral-500" name="time-outline" size={14} />
              <ThemedText className="flex-1 font-semibold text-sm text-neutral-800">
                {line}
              </ThemedText>
            </View>
          ))
        ) : (
          <ThemedText className="text-sm text-neutral-500">Agenda recorrente</ThemedText>
        )}
        <View className="flex-row items-center gap-2">
          <Ionicons className="text-neutral-500" name="calendar-outline" size={14} />
          <ThemedText className="flex-1 text-xs text-neutral-500">
            {recurrencePeriodLabel(item)}
          </ThemedText>
        </View>
        {item.price > 0 && (
          <View className="flex-row items-center gap-2">
            <Ionicons className="text-neutral-500" name="cash-outline" size={14} />
            <ThemedText className="flex-1 text-xs text-neutral-500">
              {formatCurrency(item.price)} por cobrança
            </ThemedText>
          </View>
        )}
      </View>

      <View className="flex-row gap-2.5">
        <PressableScale
          haptic
          onPress={onApprove}
          disabled={busy}
          className="flex-1 items-center rounded-full bg-emerald-600 py-3"
          style={{ opacity: busy ? 0.5 : 1 }}
        >
          <ThemedText className="font-bold text-sm text-white">Aprovar</ThemedText>
        </PressableScale>
        <PressableScale
          haptic
          onPress={onReject}
          disabled={busy}
          className="flex-1 items-center rounded-full bg-neutral-100 py-3"
          style={{ opacity: busy ? 0.5 : 1 }}
        >
          <ThemedText className="font-bold text-sm text-red-600">Recusar</ThemedText>
        </PressableScale>
      </View>
    </View>
  );
}

export default function ProviderRecurrencesScreen() {
  const { data, isLoading, refetch, isRefetching } = useProviderPendingRecurrences();
  // Deep link de notificação: o pedido avisado sobe para o topo e fica destacado.
  const [focusEnrollmentId] = useFocusParam(FOCUS_PARAM.enrollment);
  const list = React.useMemo(() => {
    const items = data ?? [];
    if (!focusEnrollmentId) return items;
    return [...items].sort((a, b) =>
      a.id === focusEnrollmentId ? -1 : b.id === focusEnrollmentId ? 1 : 0,
    );
  }, [data, focusEnrollmentId]);

  return (
    <PageLayout
      title="Recorrências pendentes"
      onBack={() => router.back()}
      scrollProps={{
        refreshControl: <RefreshControl refreshing={isRefetching} onRefresh={refetch} />,
      }}
    >
      <Pressable
        onPress={() => router.push('/(tabs)/(provider)/services')}
        className="flex-row items-center justify-between rounded-2xl bg-card p-4"
        style={{ borderCurve: 'continuous' }}
      >
        <View className="flex-1 pr-3">
          <ThemedText className="font-semibold text-sm text-neutral-900">
            Quem aceita recorrência
          </ThemedText>
          <ThemedText className="text-xs text-neutral-500">
            Cada serviço define isso na própria tela, junto com duração e cobrança.
          </ThemedText>
        </View>
        <Ionicons className="text-neutral-400" name="chevron-forward" size={18} />
      </Pressable>
      {isLoading ? (
        <ActivityIndicator style={{ marginTop: 32 }} />
      ) : list.length === 0 ? (
        <EmptyState
          icon="repeat-outline"
          title="Nada pendente"
          description="Pedidos de recorrência automática aparecem aqui para aprovação."
        />
      ) : (
        list.map((item) => (
          <PendingCard key={item.id} item={item} focused={item.id === focusEnrollmentId} />
        ))
      )}
    </PageLayout>
  );
}
